/**
 * ComponentManager handles mounting, updating, and unmounting of UI components.
 * Provides lifecycle hooks and debug logging for each phase.
 *
 * @example
 * ```ts
 * const manager = new ComponentManager('root', renderWidget, logger)
 *   .onMount(() => console.log('🔧 Component mounted'))
 *   .onUpdate(props => console.log('🔄 Props updated:', props))
 *   .onUnmount(() => console.log('🗑️ Component unmounted'));
 *
 * await manager.render({ foo: 1 });
 * await manager.render({ foo: 2 });
 * manager.unmount();
 * ```
 */
import { Logger } from '../logger';
import { RenderHandler, RenderProps } from '../types';

type LifecycleEvent = 'mount' | 'update' | 'unmount';
type LifecycleHandler = (props?: RenderProps) => void;

export class ComponentManager<T = unknown> {
  // stores handlers for each lifecycle event; ensures uniqueness
  private _lifecycleHandlers: Map<LifecycleEvent, Set<LifecycleHandler>> =
    new Map([
      ['mount', new Set()],
      ['update', new Set()],
      ['unmount', new Set()],
    ]);

  private _containerId: string; // ID of root HTML container
  private _renderHandler: RenderHandler; // strategy to mount component
  private _logger: Logger; // logger for debug output

  /** currently mounted element, null if none */
  element: T | null = null;

  /** update component props or throw if not mounted */
  updateProps!: (props: RenderProps) => void;
  /** unmount component or throw if not mounted */
  unmount!: () => void;

  /**
   * @param containerId ID of DOM element to render into
   * @param renderHandler async function that mounts and returns control methods
   * @param logger logging utility for debug tracing
   */
  constructor(
    containerId: string,
    renderHandler: RenderHandler,
    logger: Logger,
  ) {
    this._containerId = containerId;
    this._renderHandler = renderHandler;
    this._logger = logger;
  }

  /**
   * Mounts or updates the component with given props.
   * Calls appropriate lifecycle hooks and logs each phase.
   * @param props data passed to component on render/update
   */
  async render(props: RenderProps = {}): Promise<void> {
    if (!this.element) {
      this._logger.debug('mounting component', props);
      const result = await this._renderHandler(this._containerId, props);
      this.element = result?.element ?? null;

      // setup props updater: logs and triggers update handlers
      this.updateProps = (newProps: RenderProps) => {
        result?.updateProps(newProps);
        this._logger.debug('updating props', newProps);
        this._trigger('update', newProps);
      };

      // setup unmount: logs and triggers unmount handlers
      this.unmount = () => {
        this._logger.debug('unmounting component');
        result?.unmount();
        this._trigger('unmount');
        this.element = null;
      };

      this._trigger('mount');
    } else {
      this.updateProps(props);
    }
  }

  /**
   * Registers a callback to run after mounting.
   * @param callback no-args handler
   */
  onMount(callback: () => void): this {
    this._lifecycleHandlers.get('mount')!.add(callback);
    return this;
  }

  /**
   * Registers a callback to run after props update.
   * @param callback receives new props
   */
  onUpdate(callback: (props: RenderProps) => void): this {
    this._lifecycleHandlers.get('update')!.add(callback as LifecycleHandler);
    return this;
  }

  /**
   * Registers a callback to run when unmounting.
   * @param callback no-args handler
   */
  onUnmount(callback: () => void): this {
    this._lifecycleHandlers.get('unmount')!.add(callback);
    return this;
  }

  /**
   * Internal: triggers all handlers for an event.
   * @param event lifecycle phase to trigger
   * @param props optional props for update handlers
   */
  private _trigger(event: LifecycleEvent, props?: RenderProps): void {
    const handlers = this._lifecycleHandlers.get(event)!;
    handlers.forEach(fn => fn(props));
  }
}
